/**
 * Weapon definitions and the damage model.
 *
 * Shared rather than server-only because the client needs the same numbers to draw a tracer,
 * predict its own fire rate, show ammo and time a reload — but note what is *not* here: the
 * client never decides whether a shot hit. Hit resolution is server-side and lag-compensated
 * (`realtime/src/sim/combat.ts`).
 *
 * Every number below is the balance contract in `design/gdd/weapons.md` §11.4. That document
 * shows the arithmetic for each TTK; this file is only allowed to disagree with it by being
 * updated at the same time. The tests pin the contract in both directions.
 *
 * The one rule that recurs: **RPM is quantised by the 60Hz tick.** `fireIntervalTicks` rounds
 * up, so the rate a weapon actually fires at is `3600 / ceil(3600 / rpm)`, never the number a
 * tuner typed. Most of the roster was chosen to land on exact tick boundaries; the SMG was
 * deliberately not, so the effect stays visible somewhere rather than being quietly designed
 * around everywhere.
 */
import { MOVEMENT } from './movement.js';
import { HIT_ZONE, type HitZone } from './hitscan.js';
import { TICK_RATE } from './protocol.js';

export interface WeaponDefinition {
  id: number;
  name: string;
  /** Damage at or inside `falloffStartM`. Named `damage` because it always was. */
  damage: number;
  /** Damage at or beyond `falloffEndM`. Never above `damage`. */
  damageFar: number;
  /** Last range with no falloff at all, in metres. */
  falloffStartM: number;
  /** Range at which falloff bottoms out, in metres. */
  falloffEndM: number;
  /** Rounds per minute as designed. The real rate is quantised — see `realRpm`. */
  rpm: number;
  /** Beyond this the ray simply stops; nothing to hit. */
  rangeM: number;
  magazine: number;
  reloadMs: number;
  /** Cone half-angle applied to aim, in degrees, hip-fired and aimed. */
  hipSpreadDeg: number;
  adsSpreadDeg: number;
  /**
   * How hard movement widens the cone. See `spreadDegrees`.
   *
   * The most asymmetric number in the roster, and deliberately so: the SMG's low value is
   * its actual close-range advantage — its lab TTK is *slower* than the AR's, and this is
   * the lever that lets it win a real duel between two strafing players. The sniper's high
   * value is the price of a guaranteed one-shot head kill.
   */
  moveSpreadK: number;
  /** Fractional cone growth per shot in a sustained burst, and how many shots it accrues over. */
  bloomPerShot: number;
  bloomCapShots: number;
  /** Idle time, in ms, after which the burst counter resets and first-shot accuracy returns. */
  spreadResetMs: number;
  /** Movement speed multiplier while this weapon is held, and while aiming. */
  moveMultHeld: number;
  moveMultAds: number;
  /** How long raising or lowering the sights takes, in ms. Quantised to ticks. */
  adsTransitionMs: number;
  /**
   * Field of view at full ADS, as a fraction of the FOV the camera would otherwise be at.
   *
   * Omitted means `DEFAULT_ADS_FOV_SCALE` — the restrained 0.74 the whole roster shared when
   * there was one number for every weapon. It lives here rather than in the client's
   * `movement-feel.ts` because it is a balance number, not a presentation one: it decides how
   * far a weapon can realistically be shot, which is the same question `falloffEndM` answers
   * from the other side. Presentation reads it; nothing predicts from it.
   */
  adsFovScale?: number;
  /**
   * True for a weapon aimed through an optic rather than over iron sights.
   *
   * Drives presentation only — the scope mask, the reticle, and hiding the viewmodel behind
   * the optic — but it is declared beside `adsFovScale` because the two are one decision: a
   * 4x zoom with no mask reads as a bug, and a mask on a 1.35x zoom reads as a joke.
   */
  scoped?: boolean;
  /** Shotguns only: pellets per shell, and their dispersion cones. */
  pelletCount?: number;
  pelletConeHipDeg?: number;
  pelletConeAdsDeg?: number;
  /**
   * Thrown by hand rather than fired from a barrel.
   *
   * Declared rather than inferred from the id, because everything that asks this question
   * asks it about the *muzzle*, and `damage` answers a different question for a thrown
   * weapon: the grenade's 100 describes a blast, not a round leaving a barrel. Anything
   * deriving a muzzle effect from `damage` — recoil today, a shell ejection or a barrel
   * heat-haze later — has to exclude these, and an `id === GRENADE.id` check at each call
   * site is one more place to forget when the second thrown weapon arrives.
   */
  thrown?: boolean;
  /**
   * Fitted with a suppressor. Presentation and intel only — damage, spread and range are the
   * row's own numbers, and the server's hit resolution never reads this.
   *
   * What it changes: a suppressed shot does NOT ping the shooter on enemy minimaps (`main.ts`
   * gates the enemy ping on it) and plays quieter to everyone else. What it does not change:
   * the shot event still goes out — everyone in the room still hears *something*, and the
   * tracer still draws. A gun that could kill with no wire evidence at all would be an
   * anti-cheat gap, not a feature.
   */
  suppressed?: boolean;
}

/**
 * Damage multiplier per hit zone.
 *
 * ×2.5 on the head is high by military-shooter standards and deliberately so: it is what
 * makes the sniper a one-shot weapon at every range in the arena and gives the AR a two-shot
 * kill for a player who can track a head. Aim is the skill this game rewards, so aim is where
 * the reward goes. Limbs at ×0.75 exist to make a clipped shot feel like a clipped shot
 * rather than a full one.
 */
export const ZONE_MULTIPLIER: Record<HitZone, number> = {
  [HIT_ZONE.head]: 2.5,
  [HIT_ZONE.torso]: 1,
  [HIT_ZONE.limb]: 0.75,
};

/**
 * The AR, which is the P1 rifle with the P2 fields filled in.
 *
 * Damage, RPM, magazine, reload and range are numerically identical to the weapon P1 shipped
 * and measured. That is a deliberate compatibility call: `hitreg:check`, the balance the
 * arena was tuned around, and every P0/P1 number stay valid, and only the fields that did not
 * exist before are new.
 */
export const AR: WeaponDefinition = {
  id: 0,
  name: 'AR',
  damage: 34,
  damageFar: 20,
  falloffStartM: 15,
  falloffEndM: 50,
  rpm: 600,
  rangeM: 200,
  magazine: 30,
  reloadMs: 2000,
  hipSpreadDeg: 2.4,
  adsSpreadDeg: 0.3,
  moveSpreadK: 0.9,
  bloomPerShot: 0.15,
  bloomCapShots: 6,
  spreadResetMs: 400,
  moveMultHeld: 1,
  moveMultAds: 0.85,
  adsTransitionMs: 200,
};

/**
 * The SMG loses the lab TTK race to the AR (0.333s against 0.20s point blank) and still owns
 * close quarters, which is the whole design.
 *
 * It wins on the things a TTK table cannot show: the tightest hip spread in the set, so it
 * does not need to aim; the only above-1.0 movement multiplier, so it closes distance; and a
 * horizontal recoil spread that makes it useless past 25m, so owning close range costs it
 * everything else.
 */
export const SMG: WeaponDefinition = {
  id: 1,
  name: 'SMG',
  damage: 24,
  damageFar: 11,
  falloffStartM: 8,
  falloffEndM: 25,
  // 750 does not divide 3600, so this fires at 720. Kept as the roster's one visible
  // quantisation case rather than rounded away.
  rpm: 750,
  rangeM: 90,
  magazine: 25,
  reloadMs: 1700,
  hipSpreadDeg: 1.6,
  adsSpreadDeg: 0.55,
  moveSpreadK: 0.4,
  bloomPerShot: 0.1,
  bloomCapShots: 8,
  spreadResetMs: 350,
  moveMultHeld: 1.03,
  moveMultAds: 0.92,
  adsTransitionMs: 120,
};

/**
 * The shotgun is a wall, not a curve.
 *
 * Its falloff is steep and short because a shotgun that stays weakly useful at 30m is a
 * weapon nobody has to respect the range of. Past roughly 20m the cone is wider than a player
 * and the arithmetic does the rest — most pellets simply miss, with no accuracy roll involved.
 */
export const SHOTGUN: WeaponDefinition = {
  id: 2,
  name: 'shotgun',
  // Per pellet. Eight pellets is 120 at point blank: a one-shot kill if they all land.
  damage: 15,
  damageFar: 5,
  falloffStartM: 6,
  falloffEndM: 18,
  rpm: 80,
  rangeM: 40,
  magazine: 6,
  reloadMs: 2600,
  hipSpreadDeg: 0,
  adsSpreadDeg: 0,
  moveSpreadK: 0.3,
  bloomPerShot: 0,
  bloomCapShots: 0,
  spreadResetMs: 400,
  moveMultHeld: 0.97,
  moveMultAds: 0.9,
  adsTransitionMs: 250,
  pelletCount: 8,
  pelletConeHipDeg: 4,
  pelletConeAdsDeg: 2,
};

/**
 * The sniper kills in one shot to the head or the torso at any range, and never in one to a
 * limb.
 *
 * The gap between those two outcomes is the point: at 40rpm a shot that clips an arm gives
 * the target 1.5 seconds, and a sprinting player covers 14 metres in that time. The weapon is
 * decided entirely by whether the first shot found the body.
 *
 * It is the roster's only optic (`scoped`), and the only weapon whose ADS zoom is not the
 * shared 0.74 — 0.30 of a 90 degree vertical baseline is 27 degrees, roughly 3.3x. That is
 * what makes the 60m+ sightlines in `design/gdd/weapons.md` §11.2 shootable rather than
 * theoretical, and it is paid for on the other side: 6 degree hip spread, 0.45x ADS movement,
 * and a mask that removes the peripheral vision every other weapon keeps.
 */
export const SNIPER: WeaponDefinition = {
  id: 3,
  name: 'sniper',
  // Flat 110 everywhere the maps allow a shot: the falloff band starts at 150m and the
  // longest sightline on shipped content is 78.6m, so this weapon has no effective falloff.
  // Kept as a band rather than removed, because a later map may reach that far and a weapon
  // with no falloff at all is a different balance problem — and `damageFar` is 105 rather
  // than the old 80 so that even out there the body shot stays lethal.
  //
  // Above `MAX_HEALTH`, deliberately, and the single most consequential number in the file:
  // the sniper one-shots a torso at every range, and one-shots nothing else below the head.
  // Limbs land at floor(110 x 0.75) = 82, so a shot that clips an arm or a leg takes a
  // second one — 1.5s later, which is 14m of sprint. The weapon's whole skill expression is
  // where on the body the shot lands, and that only reads as a decision if the limb row is
  // survivable.
  damage: 110,
  damageFar: 105,
  falloffStartM: 150,
  falloffEndM: 250,
  rpm: 40,
  rangeM: 300,
  magazine: 5,
  reloadMs: 3000,
  // Unusable from the hip on purpose: this weapon should never win a surprised close-range
  // fight it did not set up.
  hipSpreadDeg: 6,
  adsSpreadDeg: 0.05,
  moveSpreadK: 2.5,
  bloomPerShot: 0,
  bloomCapShots: 0,
  // STRICTLY below the 1500ms fire interval, and the margin is load-bearing. At 1500 the
  // burst window sat exactly on the fire interval (both 90 ticks), so at max fire rate every
  // follow-up shot was a coin flip on tick jitter: the client's counter said "fresh burst, no
  // recoil" while the server's said "second shot" and quietly added recoilOffset(1) — 2.79°
  // of climb the crosshair never showed, two metres of miss at 40m. 1200ms keeps both sides
  // agreeing the burst is over (18 ticks of margin dwarfs input-queue jitter), and changes no
  // balance: a 40rpm weapon could never actually stack bloom inside its own fire interval.
  spreadResetMs: 1200,
  moveMultHeld: 0.9,
  moveMultAds: 0.45,
  adsTransitionMs: 350,
  // ~3.3x against the camera's 90 degree vertical baseline. Chosen against the map rather
  // than against a magnification chart: at 27 degrees vertical a standing player at 78.6m —
  // the longest clear line on shipped content — subtends enough screen to place a crosshair
  // on a head, which at the shared 0.74 they do not.
  adsFovScale: 0.3,
  scoped: true,
};

/**
 * The carbine, added to close the gap the roster left between the SMG and the AR.
 *
 * Not a gap in *reach* — the AR's falloff already starts past where the SMG's has bottomed
 * out — but a gap in *outcome*: at every one of §11.2's measured bands the jump from the AR's
 * body-shot numbers to the SMG's is a cliff, not a slope (32m: 4 shots/0.30s against 10
 * shots/0.75s). The carbine's own numbers sit arithmetically between the two at all four
 * bands (`design/gdd/weapons.md` §11.4f), by design rather than coincidence: same fire
 * interval as the AR (600rpm, exact tick boundary) so the two are directly comparable, and a
 * damage/falloff curve tuned so every STK and TTK in the table lands strictly inside the
 * AR-SMG range.
 */
export const CARBINE: WeaponDefinition = {
  id: 5,
  name: 'carbine',
  damage: 26,
  damageFar: 14,
  falloffStartM: 10,
  falloffEndM: 35,
  rpm: 600,
  rangeM: 150,
  magazine: 26,
  reloadMs: 1900,
  hipSpreadDeg: 2.0,
  adsSpreadDeg: 0.4,
  moveSpreadK: 0.65,
  bloomPerShot: 0.13,
  bloomCapShots: 7,
  spreadResetMs: 380,
  moveMultHeld: 1.0,
  moveMultAds: 0.88,
  adsTransitionMs: 150,
};

/**
 * The LMG, the roster's sustained-fire / suppression option.
 *
 * Its case is not made in TTK — every body-shot number in `design/gdd/weapons.md` §11.4g is
 * at or behind the AR's, and that is deliberate: this weapon is not supposed to win a duel
 * faster than the AR, it is supposed to keep winning the *same* duel for longer than the AR
 * can. A 90-round magazine (3x the AR's 30) and a bloom cap more than triple the AR's (20
 * shots against 6) mean it can hold accurate fire on a doorway through most of a p50-p75
 * engagement window (19-28m, §11.2) without the reload or the bloom reset that would force an
 * AR to pause. The cost is the worst movement multipliers in the hitscan roster (0.85 held,
 * 0.55 ADS) and the loosest hip spread short of the sniper's — this is a weapon you set up
 * with, not one you challenge a flank with.
 */
export const LMG: WeaponDefinition = {
  id: 6,
  name: 'LMG',
  damage: 24,
  damageFar: 16,
  falloffStartM: 20,
  falloffEndM: 55,
  rpm: 900,
  rangeM: 180,
  magazine: 90,
  reloadMs: 4200,
  hipSpreadDeg: 3.0,
  adsSpreadDeg: 0.5,
  moveSpreadK: 1.1,
  bloomPerShot: 0.05,
  bloomCapShots: 20,
  spreadResetMs: 500,
  moveMultHeld: 0.85,
  moveMultAds: 0.55,
  adsTransitionMs: 400,
};

/** Never the best weapon at any range, never dead at any range. That is the sidearm role.
 * Named "Deagle" 2026-09-11 because that is the mesh it wears (`SM_Wep_Pistol_Heavy_01`, the
 * pack's only semi-auto handgun); the twelve-round fast profile is unchanged. The constant and
 * the audio/i18n keys stay `pistol`. */
export const PISTOL: WeaponDefinition = {
  id: 4,
  name: 'Deagle',
  damage: 24,
  damageFar: 14,
  falloffStartM: 20,
  falloffEndM: 45,
  rpm: 400,
  rangeM: 120,
  magazine: 12,
  reloadMs: 1200,
  hipSpreadDeg: 1.8,
  adsSpreadDeg: 0.25,
  moveSpreadK: 0.5,
  bloomPerShot: 0.12,
  bloomCapShots: 5,
  spreadResetMs: 300,
  // Best mobility in the roster, no exception, and the fastest sights. That combination is
  // the sidearm's whole reason to exist: it is never the best weapon at any range, so what
  // it offers is the ability to *get* somewhere and to answer a surprise faster than the
  // primary you were holding.
  moveMultHeld: 1.05,
  moveMultAds: 0.97,
  adsTransitionMs: 100,
};

/**
 * The battle rifle: the roster's answer to "nothing burst-fire", built entirely from fields
 * that already exist rather than a new trigger mode.
 *
 * A hardware-enforced 3-round burst (fixed rounds, mandatory cooldown between pulls) needs a
 * new fire-mode in `shared/src/weapon-state.ts` — `stepWeapon` only knows one shot per
 * `fireSeq` today — and that is a mechanic change, not a data change, so it is out of scope
 * for this batch and flagged in `design/gdd/weapons.md` §11.4h as a `weapon-state.ts` touch
 * point for whoever picks it up next. Shipping this row with `rpm` alone and calling it a
 * burst weapon would be a lie the moment someone puts it in a loadout: `stepWeapon` would fire
 * it fully automatic at that RPM, not in bursts.
 *
 * So the gap is closed with tuning instead: the roster's hardest-hitting automatic (32 near,
 * matched only by the AR) paired with the roster's steepest, fastest-capping bloom
 * (`bloomPerShot: 0.35`, `bloomCapShots: 3`) — the third consecutive shot in under a second is
 * already at maximum spread, and a 250ms reset rewards a trigger that lets go rather than
 * holds. Nothing stops a player from holding the trigger; the numbers stop it from being
 * their best option. Its ADS spread (0.22°) is the tightest of any non-sniper weapon, which is
 * the other half of the identity: a controlled-fire rifle for the roster's longest hitscan
 * range short of the sniper's.
 */
export const BATTLE_RIFLE: WeaponDefinition = {
  id: 7,
  name: 'battle rifle',
  damage: 32,
  damageFar: 20,
  falloffStartM: 20,
  falloffEndM: 60,
  rpm: 450,
  rangeM: 220,
  magazine: 24,
  reloadMs: 2100,
  hipSpreadDeg: 2.6,
  adsSpreadDeg: 0.22,
  moveSpreadK: 1.0,
  bloomPerShot: 0.35,
  bloomCapShots: 3,
  spreadResetMs: 250,
  moveMultHeld: 0.92,
  moveMultAds: 0.8,
  adsTransitionMs: 220,
};

/**
 * The utility slot's grenade, which is a weapon only in the sense that it occupies a slot.
 *
 * Its `damage` is the value at the centre of the blast and its `damageFar` is the value at the
 * edge, so the shared falloff curve does the right thing with `falloffStartM: 0` — the same
 * `damageAtRange` every hitscan weapon uses, applied to distance from the detonation instead
 * of distance from the muzzle. One curve, not two.
 *
 * `magazine` is how many you carry, `reloadMs` is irrelevant and never used (there is no
 * resupply), and `rpm` gives the minimum interval between throws.
 */
export const GRENADE: WeaponDefinition = {
  // 8: the wire's weapon field was widened from 3 to 4 bits in this batch specifically to fit
  // this roster (5, 6 and 7 went to the carbine, LMG and battle rifle) — see `weapons.md`
  // §11.10 for the measurement that made widening it free rather than a budget fight.
  id: 8,
  name: 'grenade',
  damage: 100,
  damageFar: 1,
  falloffStartM: 0,
  falloffEndM: 6,
  // 60rpm = one throw per second minimum, which the two-grenade magazine makes academic.
  rpm: 60,
  rangeM: 6,
  magazine: 2,
  reloadMs: 0,
  hipSpreadDeg: 0,
  adsSpreadDeg: 0,
  moveSpreadK: 0,
  bloomPerShot: 0,
  bloomCapShots: 0,
  spreadResetMs: 400,
  moveMultHeld: 1.05,
  moveMultAds: 1,
  adsTransitionMs: 150,
  thrown: true,
};


/**
 * Indexed by id — `weaponById` relies on that, so order is not cosmetic.
 *
 * The grenade sits last, at index 8, and is deliberately included: `weaponById` must resolve
 * it, or a loadout's utility slot silently falls back to the AR and the utility button fires
 * a rifle.
 *
 * Nine entries, not eight: `BITS.weapon` (`shared/src/protocol.ts`) packs a weapon id into the
 * entity snapshot's status field and the kill-feed event, and was 3 bits (ids 0-7) when the
 * carbine and LMG took the roster to exactly that ceiling. Adding the battle rifle needed a
 * ninth id, so the field was widened to 4 bits (0-15) — measured first, in
 * `design/gdd/weapons.md` §11.10, as free against both gated downstream budgets and a couple
 * of bytes against the ungated one. Seven ids (9-15) are spare after this batch.
 */
// ─── The second roster (2026-09-04, ids 9-14) ─────────────────────────────────────────────
// Six more Synty models given roles the first nine left open: a heavy sidearm, a tighter SMG,
// a hard-hitting rifle, a semi-auto marksman rifle, an anti-materiel rifle and a light scout.
// Every RPM below divides 3600 evenly or is documented where it does not; every reset window
// clears its fire interval by ≥100ms (`spread.test.ts`). Balance numbers follow §11's model
// and have NOT been playtested — they are the first draft the playtest will retune.

/** Revolver — the heavy sidearm: six rounds that hurt against the Deagle's twelve fast ones.
 * Named "revolver" in the balance draft (weapons.md §12), renamed "Deagle" 2026-09-07 with the
 * loadout rework, and named back 2026-09-11: the mesh is `SM_Wep_Pistol_Revolver_01`, a
 * long-barrel .357, and the pack's actual Desert Eagle silhouette (`SM_Wep_Pistol_Heavy_01`) is
 * what id 4 has worn since the first import. Names follow the meshes a player sees; the id and
 * the numbers did not move. */
export const REVOLVER: WeaponDefinition = {
  id: 9,
  name: 'revolver',
  damage: 60,
  damageFar: 40,
  falloffStartM: 15,
  falloffEndM: 45,
  rpm: 150, // 24 ticks
  rangeM: 120,
  magazine: 6,
  reloadMs: 2200,
  hipSpreadDeg: 2.2,
  adsSpreadDeg: 0.2,
  moveSpreadK: 0.6,
  bloomPerShot: 0.3,
  bloomCapShots: 3,
  spreadResetMs: 700,
  moveMultHeld: 1.05,
  moveMultAds: 0.95,
  adsTransitionMs: 120,
};

/** PDW — the SMG's smaller sibling: less damage, tighter, faster on the sights. */
export const PDW: WeaponDefinition = {
  id: 10,
  name: 'PDW',
  damage: 22,
  damageFar: 12,
  falloffStartM: 10,
  falloffEndM: 30,
  rpm: 800, // ceil(4.5) = 5 ticks -> 720 real, same as the SMG
  rangeM: 100,
  magazine: 30,
  reloadMs: 1600,
  hipSpreadDeg: 1.4,
  adsSpreadDeg: 0.45,
  moveSpreadK: 0.35,
  bloomPerShot: 0.09,
  bloomCapShots: 8,
  spreadResetMs: 350,
  moveMultHeld: 1.02,
  moveMultAds: 0.95,
  adsTransitionMs: 120,
};

/** G3 — the AR's heavier answer: three body shots instead of three-to-four, at the cost of rate and bloom. */
export const G3: WeaponDefinition = {
  id: 11,
  name: 'G3',
  damage: 40,
  damageFar: 26,
  falloffStartM: 12,
  falloffEndM: 45,
  rpm: 450, // 8 ticks
  rangeM: 200,
  magazine: 30,
  reloadMs: 2300,
  hipSpreadDeg: 2.8,
  adsSpreadDeg: 0.35,
  moveSpreadK: 1.0,
  bloomPerShot: 0.25,
  bloomCapShots: 5,
  spreadResetMs: 450,
  moveMultHeld: 0.97,
  moveMultAds: 0.85,
  adsTransitionMs: 220,
};

/** DMR — semi-auto marksman rifle with a 2x optic: two body shots at any range, no one-shot. */
export const DMR: WeaponDefinition = {
  id: 12,
  name: 'DMR',
  damage: 62,
  damageFar: 50,
  falloffStartM: 40,
  falloffEndM: 120,
  rpm: 200, // 18 ticks
  rangeM: 260,
  magazine: 10,
  reloadMs: 2600,
  hipSpreadDeg: 4.0,
  adsSpreadDeg: 0.1,
  moveSpreadK: 1.8,
  bloomPerShot: 0.5,
  bloomCapShots: 3,
  spreadResetMs: 600,
  moveMultHeld: 0.9,
  moveMultAds: 0.7,
  adsTransitionMs: 280,
  adsFovScale: 0.5,
  scoped: true,
};

/** Anti-materiel rifle — one shot anywhere, 2.4s between shots, three rounds, immobile aimed. */
export const HEAVY_SNIPER: WeaponDefinition = {
  id: 13,
  name: 'anti-materiel',
  damage: 150,
  damageFar: 150,
  falloffStartM: 200,
  falloffEndM: 300,
  rpm: 25, // 144 ticks
  rangeM: 400,
  magazine: 3,
  reloadMs: 4000,
  hipSpreadDeg: 8,
  adsSpreadDeg: 0.05,
  moveSpreadK: 3,
  bloomPerShot: 0,
  bloomCapShots: 0,
  spreadResetMs: 2700,
  moveMultHeld: 0.8,
  moveMultAds: 0.4,
  adsTransitionMs: 450,
  adsFovScale: 0.25,
  scoped: true,
};

/** Scout — the light sniper: head one-shots, body needs two, and it moves while it does it. */
export const SCOUT: WeaponDefinition = {
  id: 14,
  name: 'scout',
  damage: 80,
  damageFar: 75,
  falloffStartM: 100,
  falloffEndM: 200,
  rpm: 90, // 40 ticks
  rangeM: 300,
  magazine: 8,
  reloadMs: 2400,
  hipSpreadDeg: 5,
  adsSpreadDeg: 0.05,
  moveSpreadK: 2.0,
  bloomPerShot: 0,
  bloomCapShots: 0,
  spreadResetMs: 900,
  moveMultHeld: 1.0,
  moveMultAds: 0.8,
  adsTransitionMs: 250,
  adsFovScale: 0.4,
  scoped: true,
};

// ─── The third roster (2026-09-08, ids 15-20) ─────────────────────────────────────────────
// Three more sidearms and three more utility items, because a slot with one thing in it is
// not a choice (loadouts.md §1 said as much about the utility slot and now stops being true).
// The wire's weapon field went 4 -> 5 bits for these (0-31); §11.10's argument holds again —
// the field rides the rare status change-bit, never the steady-state delta. Balance is a
// first draft against §11's model, tick-quantised RPMs, and NOT playtested.

/** Snub — the fast-draw revolver: three body shots inside 10m, useless past 25, the quickest
 * sights in the roster. Between the Deagle's twelve fast rounds and the revolver's six slow ones. */
export const SNUB: WeaponDefinition = {
  id: 15,
  name: 'snub',
  damage: 42,
  damageFar: 24,
  falloffStartM: 8,
  falloffEndM: 25,
  rpm: 240, // 15 ticks
  rangeM: 80,
  magazine: 5,
  reloadMs: 1800,
  hipSpreadDeg: 2.0,
  adsSpreadDeg: 0.3,
  moveSpreadK: 0.5,
  bloomPerShot: 0.25,
  bloomCapShots: 3,
  spreadResetMs: 450,
  moveMultHeld: 1.05,
  moveMultAds: 0.97,
  adsTransitionMs: 80,
};

/** SD pistol — the pistol with a can on it: a round softer, three rounds more, and it does not
 * show up on the minimap when it fires (`suppressed`). The sidearm for a flank. */
export const SUPPRESSED_PISTOL: WeaponDefinition = {
  id: 16,
  name: 'SD pistol',
  damage: 21,
  damageFar: 12,
  falloffStartM: 15,
  falloffEndM: 40,
  rpm: 450, // 8 ticks
  rangeM: 110,
  magazine: 15,
  reloadMs: 1300,
  hipSpreadDeg: 1.6,
  adsSpreadDeg: 0.22,
  moveSpreadK: 0.45,
  bloomPerShot: 0.1,
  bloomCapShots: 5,
  spreadResetMs: 300,
  moveMultHeld: 1.05,
  moveMultAds: 0.97,
  adsTransitionMs: 100,
  suppressed: true,
};

/** Machine pistol — a full-auto sidearm: 900rpm of 17 damage with a bloom that is at its cap by
 * the sixth round. Wins the panic fight at 5m and nothing past 15. */
export const MACHINE_PISTOL: WeaponDefinition = {
  id: 17,
  name: 'machine pistol',
  damage: 17,
  damageFar: 9,
  falloffStartM: 6,
  falloffEndM: 20,
  rpm: 900, // 4 ticks
  rangeM: 70,
  magazine: 20,
  reloadMs: 1500,
  hipSpreadDeg: 2.4,
  adsSpreadDeg: 0.8,
  moveSpreadK: 0.45,
  bloomPerShot: 0.2,
  bloomCapShots: 6,
  spreadResetMs: 300,
  moveMultHeld: 1.05,
  moveMultAds: 0.95,
  adsTransitionMs: 100,
};

/**
 * Smoke — a thrown weapon that deals no damage: it pops after a short fuse and leaves a cloud
 * nobody can see through for a while (`THROWABLES`). `damage` 0 so every formula that prices a
 * hit prices it at nothing; `rangeM` is the cloud radius, the same reading the grenade gives
 * its blast.
 */
export const SMOKE: WeaponDefinition = {
  id: 18,
  name: 'smoke',
  damage: 0,
  damageFar: 0,
  falloffStartM: 0,
  falloffEndM: 5,
  rpm: 60,
  rangeM: 5,
  magazine: 2,
  reloadMs: 0,
  hipSpreadDeg: 0,
  adsSpreadDeg: 0,
  moveSpreadK: 0,
  bloomPerShot: 0,
  bloomCapShots: 0,
  spreadResetMs: 400,
  moveMultHeld: 1.05,
  moveMultAds: 1,
  adsTransitionMs: 150,
  thrown: true,
};

/** Flash — no damage, a whiteout for anyone facing it inside its radius with line of sight.
 * One carried, not two: a second flash would make every corner a coin flip. */
export const FLASH: WeaponDefinition = {
  id: 19,
  name: 'flash',
  damage: 0,
  damageFar: 0,
  falloffStartM: 0,
  falloffEndM: 14,
  rpm: 60,
  rangeM: 14,
  magazine: 1,
  reloadMs: 0,
  hipSpreadDeg: 0,
  adsSpreadDeg: 0,
  moveSpreadK: 0,
  bloomPerShot: 0,
  bloomCapShots: 0,
  spreadResetMs: 400,
  moveMultHeld: 1.05,
  moveMultAds: 1,
  adsTransitionMs: 150,
  thrown: true,
};

/**
 * Incendiary — bursts into a pool of fire that burns whoever stands in it for its duration.
 * `damage` is the pool's damage PER SECOND at the centre, `damageFar` at its edge, applied by
 * the server every `THROWABLES[…].fire.tickIntervalTicks`; one carried, and a 1.5s fuse so it
 * cannot be dropped at a doorway faster than the door can be left.
 */
export const INCENDIARY: WeaponDefinition = {
  id: 20,
  name: 'incendiary',
  damage: 14,
  damageFar: 8,
  falloffStartM: 1,
  falloffEndM: 3.5,
  rpm: 60,
  rangeM: 3.5,
  magazine: 1,
  reloadMs: 0,
  hipSpreadDeg: 0,
  adsSpreadDeg: 0,
  moveSpreadK: 0,
  bloomPerShot: 0,
  bloomCapShots: 0,
  spreadResetMs: 400,
  moveMultHeld: 1.05,
  moveMultAds: 1,
  adsTransitionMs: 150,
  thrown: true,
};

/** What a thrown weapon does when its fuse runs out. `blast` is the frag's radius damage;
 * `smoke` and `flash` are presentation the server only announces; `fire` is a zone the server
 * keeps damaging for `durationMs`. */
export type ThrowableEffect = 'blast' | 'smoke' | 'flash' | 'fire';

export interface ThrowablePhysics {
  /** Metres per second along the aim direction. */
  throwSpeed: number;
  /** Minimum launch pitch, in radians — see `GRENADE_PHYSICS.minPitch`. */
  minPitch: number;
  fuseMs: number;
  /** Energy kept per bounce. */
  restitution: number;
  /** Collision radius of the projectile itself. */
  radiusMeters: number;
  effect: ThrowableEffect;
  /** Effect radius in metres — the blast, the cloud, the flash reach, the fire pool. */
  radiusM: number;
  /** How long the effect stays in the world after the burst: the cloud, the pool, the longest
   * possible blind. Zero for a blast, which is over the instant it happens. */
  durationMs: number;
  /** Fraction of the effect's damage the thrower takes from their own throwable. */
  selfDamageMultiplier: number;
  /** Fire pools only: how often, in ticks, the server applies a slice of the per-second damage. */
  fire?: { tickIntervalTicks: number };
}

/** Blast radius and flight for the frag grenade, kept beside the definition it belongs to. */
export const GRENADE_PHYSICS: ThrowablePhysics = {
  radiusM: 6,
  /** Metres per second along the aim direction. */
  throwSpeed: 18,
  /**
   * Minimum launch pitch, in radians.
   *
   * Aiming at your own feet should still produce an arc rather than a grenade that lands on
   * them. 5° is the smallest angle that reliably clears the thrower's own capsule.
   */
  minPitch: (5 * Math.PI) / 180,
  fuseMs: 2000,
  /** Energy kept per bounce. Below 0.5 so a grenade settles rather than skittering away. */
  restitution: 0.4,
  /** Collision radius of the projectile itself. */
  radiusMeters: 0.1,
  effect: 'blast',
  durationMs: 0,
  /**
   * Fraction of blast damage the thrower takes from their own grenade.
   *
   * Not 1.0, measured and decided 2026-08-20: throws from every spawn on every map showed the
   * median detonation lands 4.7m from a stationary thrower — inside the 6m radius — for up to
   * 86 damage, so any grenade thrown while already scratched read as instant suicide (reported
   * twice from bot matches). Not 0 either: a grenade bounced off the wall in front of you
   * should still teach the lesson. Half keeps the punishment and removes the coin-flip death.
   */
  selfDamageMultiplier: 0.5,
};

/**
 * Flight and effect per thrown weapon, keyed by id. Everything the shared integrator and the
 * room's detonation need that is not in the `WeaponDefinition` row itself. A thrown weapon
 * with no entry here is a bug `throwableOf` refuses loudly, not a grenade by default.
 */
export const THROWABLES: Readonly<Record<number, ThrowablePhysics>> = {
  [GRENADE.id]: GRENADE_PHYSICS,
  [SMOKE.id]: {
    ...GRENADE_PHYSICS,
    // Pops sooner than the frag: a smoke that took two seconds would land after the fight it
    // was meant to cover. A canister is heavier than a frag and bounces less.
    fuseMs: 1500,
    restitution: 0.3,
    effect: 'smoke',
    radiusM: SMOKE.rangeM,
    durationMs: 12_000,
    selfDamageMultiplier: 0,
  },
  [FLASH.id]: {
    ...GRENADE_PHYSICS,
    fuseMs: 1200,
    effect: 'flash',
    radiusM: FLASH.rangeM,
    // The LONGEST blind — point blank, facing it, in the open. `flashBlindMs` scales it down.
    durationMs: 2200,
    selfDamageMultiplier: 0,
  },
  [INCENDIARY.id]: {
    ...GRENADE_PHYSICS,
    fuseMs: 1500,
    // A bottle does not bounce: it breaks where it lands.
    restitution: 0.05,
    effect: 'fire',
    radiusM: INCENDIARY.rangeM,
    durationMs: 6000,
    // Your own fire burns you at full rate. The frag's half-rate exists because a blast's
    // 4.7m median landing is a coin flip; a fire pool is a place, and standing in it is a choice.
    selfDamageMultiplier: 1,
    fire: { tickIntervalTicks: 15 }, // four slices per second
  },
};

/** Flight and effect for a thrown weapon. Throws for anything else: the caller asked the wrong
 * question about a gun, and the answer "it is a grenade" would fire one. */
export function throwableOf(weaponId: number): ThrowablePhysics {
  const physics = THROWABLES[weaponId];
  if (physics === undefined) throw new Error(`weapon ${weaponId} is not thrown`);
  return physics;
}

export function isThrowable(weaponId: number): boolean {
  return THROWABLES[weaponId] !== undefined;
}

/**
 * How long a flash blinds a viewer, in ms, from the geometry of the moment: full for a viewer
 * looking straight at a burst at their feet, tapering with distance and with how far the burst
 * is from the centre of their view, nothing past the radius or from behind. Pure, so every
 * client and any test agrees on the number; line of sight is the caller's question.
 *
 * `facing` is the cosine of the angle between the viewer's look direction and the direction to
 * the burst: 1 = dead ahead, 0 = at the edge of vision, negative = behind.
 */
export function flashBlindMs(distanceM: number, facing: number): number {
  const physics = THROWABLES[FLASH.id]!;
  if (distanceM >= physics.radiusM) return 0;
  // A burst behind you still flares the periphery for a moment; one in front of you blinds.
  const facingScale = facing <= 0 ? 0.15 : 0.15 + 0.85 * facing;
  const distanceScale = 1 - distanceM / physics.radiusM;
  return Math.round(physics.durationMs * facingScale * (0.35 + 0.65 * distanceScale));
}

export const WEAPONS: readonly WeaponDefinition[] = [
  AR,
  SMG,
  SHOTGUN,
  SNIPER,
  PISTOL,
  CARBINE,
  LMG,
  BATTLE_RIFLE,
  GRENADE,
  REVOLVER,
  PDW,
  G3,
  DMR,
  HEAVY_SNIPER,
  SCOUT,
  SNUB,
  SUPPRESSED_PISTOL,
  MACHINE_PISTOL,
  SMOKE,
  FLASH,
  INCENDIARY,
];

/**
 * Kept as an alias because P0 and P1 named this weapon `RIFLE` everywhere, including in
 * committed harness output and the netcode GDD. The object is the AR.
 */
export const RIFLE = AR;

export function weaponById(id: number): WeaponDefinition {
  return WEAPONS[id] ?? AR;
}

/**
 * Minimum ticks between shots, rounded **up**.
 *
 * Up, not nearest: rounding down would let a weapon fire slightly faster than its stated RPM,
 * and every rate limit in this codebase errs toward the server's advantage. At 600rpm that is
 * 10 shots/second, or one every 6 ticks.
 */
export function fireIntervalTicks(weapon: WeaponDefinition): number {
  return Math.max(1, Math.ceil((60 / weapon.rpm) * TICK_RATE));
}

/**
 * The rate a weapon actually fires at, after tick quantisation.
 *
 * Always at or below the designed `rpm`. Worth surfacing rather than leaving implicit: a
 * tuner who sets 750 and measures 720 should find the discrepancy in an API, not in a
 * spreadsheet three weeks later.
 */
export function realRpm(weapon: WeaponDefinition): number {
  return (60 * TICK_RATE) / fireIntervalTicks(weapon);
}

/** Ticks a reload occupies, rounded up for the same reason fire intervals are. */
export function reloadTicks(weapon: WeaponDefinition): number {
  return Math.max(1, Math.ceil((weapon.reloadMs / 1000) * TICK_RATE));
}

/** Ticks to raise or lower the sights, rounded up. */
export function adsTicks(weapon: WeaponDefinition): number {
  return Math.max(1, Math.ceil((weapon.adsTransitionMs / 1000) * TICK_RATE));
}

/**
 * The ADS field-of-view fraction every weapon without an optic uses.
 *
 * Restrained on purpose. A hard zoom is the cheapest way to make aiming feel powerful and the
 * fastest way to make it feel like a different game: the camera's baseline is 90 degrees
 * vertical (~121 horizontal), so 0.74 lands at ~67 vertical — enough that a distant head
 * grows into something you can place a crosshair on, small enough that peripheral vision,
 * which is how you notice a flank, survives.
 */
export const DEFAULT_ADS_FOV_SCALE = 0.74;

/** How far a weapon narrows the view at full ADS. */
export function adsFovScale(weapon: WeaponDefinition): number {
  return weapon.adsFovScale ?? DEFAULT_ADS_FOV_SCALE;
}

/** Whether the weapon is aimed through an optic, and so draws a scope rather than sights. */
export function isScoped(weapon: WeaponDefinition): boolean {
  return weapon.scoped === true;
}

/**
 * Damage before zone multipliers, at a given range.
 *
 * Linear between `falloffStartM` and `falloffEndM`, flat outside them. Not floored here: the
 * floor belongs after the zone multiplier, or a 27.2-damage body shot and its head-shot
 * equivalent round twice and drift apart.
 */
export function damageAtRange(weapon: WeaponDefinition, distanceM: number): number {
  if (distanceM <= weapon.falloffStartM) return weapon.damage;
  if (distanceM >= weapon.falloffEndM) return weapon.damageFar;

  const span = weapon.falloffEndM - weapon.falloffStartM;
  // Guaranteed positive by the definitions, but a mis-tuned weapon with start === end would
  // otherwise divide by zero and deal NaN damage — which reads as immortality, not as a bug.
  if (span <= 0) return weapon.damageFar;

  const fraction = (distanceM - weapon.falloffStartM) / span;
  return weapon.damage - (weapon.damage - weapon.damageFar) * fraction;
}

/**
 * Final integer damage for one hit: range falloff, then zone, then floor.
 *
 * Floored once, at the end. Flooring the range damage first and then multiplying would make a
 * head shot depend on a rounding decision taken before the zone was known — the same input
 * producing different totals depending on evaluation order is exactly the kind of thing that
 * turns into an unreproducible "he survived four shots" report.
 */
export function damageFor(weapon: WeaponDefinition, distanceM: number, zone: HitZone): number {
  const base = damageAtRange(weapon, distanceM);
  return Math.max(1, Math.floor(base * ZONE_MULTIPLIER[zone]));
}

/**
 * A player's health at spawn, and their maximum.
 *
 * Lives in this file because every damage figure in it is calibrated against it: the shots-to-
 * kill and time-to-kill tables, and the balance numbers in `design/gdd/weapons.md` derived from
 * them, are all "out of 100". Change this and the whole weapon table's TTK changes with it.
 *
 * Exported so the client's health bar and the server's spawn both read one number. A bar drawn
 * against a different maximum than the server heals to is wrong in the most expensive way — it
 * looks like a game state, not like a bug, and this HUD has already shipped one counter that
 * confidently reported 215 weapon corrections where there were none.
 */
export const MAX_HEALTH = 100;

/** Shots needed to kill from full health at this range and zone. */
export function shotsToKill(
  weapon: WeaponDefinition,
  distanceM: number,
  zone: HitZone,
  health = MAX_HEALTH,
): number {
  return Math.ceil(health / damageFor(weapon, distanceM, zone));
}

/**
 * Time to kill, in seconds.
 *
 * `STK - 1` intervals, not `STK`: the clock starts when the first shot lands and stops when
 * the last one does. A one-shot kill therefore takes 0s, which is the honest answer — there
 * is no second shot to wait for.
 */
export function timeToKillSeconds(
  weapon: WeaponDefinition,
  distanceM: number,
  zone: HitZone,
  health = MAX_HEALTH,
): number {
  const shots = shotsToKill(weapon, distanceM, zone, health);
  return ((shots - 1) * fireIntervalTicks(weapon)) / TICK_RATE;
}

/**
 * Expected pellets on target for a shotgun at a given range.
 *
 * A model, not the server's resolution: the server fires every pellet as an independent ray
 * and counts what lands. This exists so the balance table has a number to be checked against,
 * and so a designer can ask "what does 4° mean at 12m" without running a match.
 *
 * The square is the geometry: pellets are spread over a disc whose area grows with the square
 * of its radius, and the target occupies a fixed disc inside it.
 */
export function expectedPelletsOnTarget(
  weapon: WeaponDefinition,
  distanceM: number,
  aiming: boolean,
  targetRadiusM = MOVEMENT.radius,
): number {
  const pellets = weapon.pelletCount ?? 0;
  if (pellets === 0) return 0;

  const coneDeg = (aiming ? weapon.pelletConeAdsDeg : weapon.pelletConeHipDeg) ?? 0;
  const coneRadius = distanceM * Math.tan((coneDeg * Math.PI) / 180);
  // At point blank the cone is narrower than the target and every pellet lands.
  if (coneRadius <= targetRadiusM) return pellets;

  const fraction = (targetRadiusM / coneRadius) ** 2;
  return Math.floor(fraction * pellets);
}

/**
 * Where a shot originates, relative to the capsule centre.
 *
 * Derived from the controller's eye height rather than written down again: the capsule centre
 * is `height/2` above the feet and the eye is `eyeHeight`, so a hardcoded number here would
 * silently disagree with the camera the moment either changes. One source of truth, same rule
 * as the movement flags.
 */
export const MUZZLE_OFFSET_Y = MOVEMENT.eyeHeight - MOVEMENT.height / 2;
